/**
 * ConsentBanner - GDPR-compliant consent banner
 *
 * Displays a consent banner that allows users to accept or reject cookies and tracking.
 * Stores consent preferences in localStorage and only loads analytics if consent is given.
 *
 * Features:
 * - GDPR compliant
 * - Persistent consent storage
 * - Granular consent options (optional for future)
 * - Accessible (keyboard navigation, screen reader support)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";
import { getStoredConsent, storeConsent, type ConsentPreferences } from "@/utils/consent";
import { trapFocus, restoreFocus, getFocusedElement } from "@/utils/accessibility";
import { initAnalytics } from "@/utils/analytics";
import { initMarketingPixels } from "@/utils/marketingPixels";

// Re-export for backward compatibility
// eslint-disable-next-line react-refresh/only-export-components
export { hasConsent, hasAnalyticsConsent } from "@/utils/consent";

interface ConsentBannerProps {
  onConsentChange?: (consented: boolean) => void;
}

export default function ConsentBanner({ onConsentChange }: ConsentBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const handleAccept = useCallback(() => {
    const preferences: ConsentPreferences = {
      accepted: true,
      timestamp: Date.now(),
      analytics: true,
      marketing: true, // Accept marketing cookies when user accepts all
    };
    storeConsent(preferences);
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
    onConsentChange?.(true);

    // Initialize analytics if consent given
    if (preferences.analytics) {
      initAnalytics();
    }

    // Initialize marketing pixels if consent given
    if (preferences.marketing) {
      initMarketingPixels();
    }
  }, [onConsentChange]);

  const handleReject = useCallback(() => {
    const preferences: ConsentPreferences = {
      accepted: false,
      timestamp: Date.now(),
      analytics: false,
      marketing: false,
    };
    storeConsent(preferences);
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
    onConsentChange?.(false);
  }, [onConsentChange]);

  useEffect(() => {
    // Check if consent has already been given
    const consent = getStoredConsent();
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
        // Store current focus before showing dialog
        previousFocusRef.current = getFocusedElement();
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Consent already given, notify parent
      onConsentChange?.(consent.accepted);
    }
  }, [onConsentChange]);

  // Focus trap and Escape key handling
  useEffect(() => {
    if (!isVisible || !dialogRef.current) return;

    // Trap focus within dialog
    const cleanup = trapFocus(dialogRef.current);

    // Handle Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleReject(); // Close without accepting (treat as rejection)
      }
    };

    document.addEventListener("keydown", handleEscape);

    // Hide background content from screen readers
    const mainContent = document.querySelector("main");
    let originalTabIndex: string | null = null;
    if (mainContent) {
      // Store original tabIndex before setting aria-hidden
      originalTabIndex = mainContent.getAttribute("tabindex");
      // Remove tabIndex to prevent focus issues when aria-hidden is set
      if (mainContent.hasAttribute("tabindex")) {
        mainContent.removeAttribute("tabindex");
      }
      mainContent.setAttribute("aria-hidden", "true");
    }

    return () => {
      cleanup();
      document.removeEventListener("keydown", handleEscape);
      // Restore aria-hidden and tabIndex on main content
      if (mainContent) {
        mainContent.removeAttribute("aria-hidden");
        // Restore original tabIndex if it existed
        if (originalTabIndex !== null) {
          mainContent.setAttribute("tabindex", originalTabIndex);
        }
      }
      // Restore focus to previous element
      if (previousFocusRef.current) {
        restoreFocus(previousFocusRef.current);
      }
    };
  }, [isVisible, handleReject]);

  const handleClose = () => {
    // Close without accepting (treat as rejection)
    handleReject();
  };

  if (!isVisible) return null;

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-banner-title"
      aria-describedby="consent-banner-description"
      className={`fixed bottom-0 left-0 right-0 z-[9999] bg-[#09091e] border-t border-[rgba(255,255,255,0.12)] shadow-lg min-h-[120px] ${
        prefersReducedMotion ? "" : "transition-transform duration-300"
      } ${isAnimating ? "translate-y-0" : "translate-y-full"}`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          {/* Content */}
          <div className="flex-1">
            <h3
              id="consent-banner-title"
              className="font-sans font-medium text-white text-lg sm:text-xl mb-2"
            >
              Cookie Consent
            </h3>
            <p
              id="consent-banner-description"
              className="font-sans font-normal text-[rgba(255,255,255,0.75)] text-sm sm:text-base leading-relaxed wrap-break-word"
            >
              We use cookies to enhance your browsing experience, analyze site traffic,
              and personalize content. By clicking "Accept All", you consent to our use of
              cookies. You can learn more in our{" "}
              <a
                href="/cookie-policy"
                className="font-sans text-white underline decoration-white decoration-2 underline-offset-2 hover:no-underline hover:text-[rgba(255,255,255,0.9)] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e] rounded transition-all"
              >
                Cookie Policy
              </a>
              .
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={handleReject}
              className="px-4 py-2 sm:px-6 sm:py-3 rounded-full bg-[rgba(255,255,255,0.1)] text-white font-sans font-medium text-sm sm:text-base hover:bg-[rgba(255,255,255,0.15)] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e] min-h-[44px]"
            >
              Reject All
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 sm:px-6 sm:py-3 rounded-full bg-white text-[rgba(7,7,8,0.9)] font-sans font-medium text-sm sm:text-base hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e] min-h-[44px]"
            >
              Accept All
            </button>
            <button
              onClick={handleClose}
              className="p-2 text-[rgba(255,255,255,0.75)] hover:text-white hover:bg-[rgba(255,255,255,0.1)] rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e] min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close consent banner"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
